import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import Cookies from 'js-cookie';
import type { ConfigState, LatLng, TimeFormat, FontSize, Locale } from '@/types';

interface ConfigActions {
  setLocation: (latLng: LatLng) => void;
  setTitle: (title: string) => void;
  setTimeFormat: (format: TimeFormat) => void;
  updateRouteOrder: (order: string[]) => void;
  hideRoute: (routeId: string) => void;
  unhideRoute: (routeId: string) => void;
  setSearchRadius: (meters: number) => void;
  setAutoscroll: (enabled: boolean, interval?: number) => void;
  setRoutesPerRow: (count: number) => void;
  setFontSize: (size: FontSize) => void;
  setLocale: (locale: Locale) => void;
  importConfig: (config: Partial<ConfigState>) => void;
  exportConfig: () => ConfigState;
  reset: () => void;
}

// Custom storage adapter for cookies (matching AngularJS behavior)
const cookieStorage = {
  getItem: (name: string) => {
    const value = Cookies.get(name);
    return value ?? null;
  },
  setItem: (name: string, value: string) => {
    Cookies.set(name, value, { expires: 365 });
  },
  removeItem: (name: string) => {
    Cookies.remove(name);
  },
};

const DEFAULT_CONFIG: ConfigState = {
  id: '',
  title: '',
  latLng: { latitude: 41.4995, longitude: -81.6954 }, // Public Square, Cleveland
  timeFormat: 'HH:mm',
  routeOrder: [],
  hiddenRoutes: [],
  searchRadiusMeters: 1000,
  autoscrollEnabled: false,
  autoscrollInterval: 10,
  routesPerRow: 4,
  fontSize: 'normal',
  locale: 'en',
};

export const useConfigStore = create<ConfigState & ConfigActions>()(
  persist(
    (set, get) => ({
      ...DEFAULT_CONFIG,

      setLocation: (latLng) => set({ latLng }),

      setTitle: (title) => set({ title }),

      setTimeFormat: (timeFormat) => set({ timeFormat }),

      updateRouteOrder: (routeOrder) => set({ routeOrder }),

      hideRoute: (routeId) =>
        set((state) => ({
          hiddenRoutes: state.hiddenRoutes.includes(routeId)
            ? state.hiddenRoutes
            : [...state.hiddenRoutes, routeId],
        })),

      unhideRoute: (routeId) =>
        set((state) => ({
          hiddenRoutes: state.hiddenRoutes.filter((id) => id !== routeId),
        })),

      setSearchRadius: (searchRadiusMeters) => set({ searchRadiusMeters }),

      setAutoscroll: (enabled, interval) =>
        set({
          autoscrollEnabled: enabled,
          ...(interval !== undefined && { autoscrollInterval: interval }),
        }),

      setRoutesPerRow: (routesPerRow) => set({ routesPerRow }),

      setFontSize: (fontSize) => set({ fontSize }),

      setLocale: (locale) => set({ locale }),

      importConfig: (config) => set((state) => ({ ...state, ...config })),

      exportConfig: () => {
        const state = get();
        return {
          id: state.id,
          title: state.title,
          latLng: state.latLng,
          timeFormat: state.timeFormat,
          routeOrder: state.routeOrder,
          hiddenRoutes: state.hiddenRoutes,
          searchRadiusMeters: state.searchRadiusMeters,
          autoscrollEnabled: state.autoscrollEnabled,
          autoscrollInterval: state.autoscrollInterval,
          routesPerRow: state.routesPerRow,
          fontSize: state.fontSize,
          locale: state.locale,
        };
      },

      reset: () => set(DEFAULT_CONFIG),
    }),
    {
      name: 'config', // Cookie name (matches AngularJS)
      storage: createJSONStorage(() => cookieStorage),
    }
  )
);
